import type { StatusHistoryEntry } from '../../api/types';
import { formatDateTime } from '../../lib/format';
import { useVocabulary } from '../../vocab/VocabularyProvider';
import { StatusChip } from '../ticket/StatusChip';

/**
 * ⭐⭐ `ticket_status_history` is THE ARTEFACT; `ticket.status` is merely its last row (db/001).
 *
 * The from→to pair is rendered explicitly and never collapsed to "moved to Failed on UAT", because
 * "failed after QA WIP" and "failed after PO review" are different failures and the current status
 * cannot tell them apart. That is the entire reason this table exists.
 */
export function StatusHistory({ entries }: { entries: StatusHistoryEntry[] }) {
  const vocab = useVocabulary();
  const ordered = [...entries].sort(
    (a, b) => new Date(b.changed_at).getTime() - new Date(a.changed_at).getTime(),
  );

  return (
    <ol className="space-y-2.5">
      {ordered.map((entry) => (
        <li key={entry.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
          {entry.from_status ? (
            <>
              <StatusChip status={vocab.status(entry.from_status)} className="max-w-[8rem]" />
              <span aria-hidden="true" className="text-[color:var(--color-ink-subtle)]">
                →
              </span>
            </>
          ) : (
            <span className="text-xs text-[color:var(--color-ink-muted)]">created as</span>
          )}
          <StatusChip status={vocab.status(entry.to_status)} className="max-w-[8rem]" />
          <span className="text-xs text-[color:var(--color-ink-muted)]">
            {entry.changed_by} · {formatDateTime(entry.changed_at)}
          </span>
          {entry.note ? (
            <span className="basis-full text-xs text-[color:var(--color-ink-subtle)] italic">
              {entry.note}
            </span>
          ) : null}
        </li>
      ))}
    </ol>
  );
}
